import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Backdrop,
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { fetchAdmin, toErrorMessage } from '../../admin/api';
import { AdminRouteLayout } from './AdminRoutePageShared';
import { AdminErrorState, PolicyInspectorDrawer } from '../../sections/admin/shared';

const TRANSACTION_TYPES = ['bank_transfer', 'crypto_settlement_handoff'];
const DECISION_CODES = [
  'beneficiary.third_party.limit_exceeded',
  'user_control.blocked',
  'policy.allow',
];
const TIER_CODES = ['stage_zero', 'stage_one', 'stage_two', 'stage_three'];

function getStatusLabel(row) {
  if (String(row.requires_review) === '1') return 'Needs review';
  if (String(row.allowed) === '1') return 'Resolved';
  return 'Blocked';
}

function getStatusColor(row) {
  if (String(row.requires_review) === '1') return 'warning.main';
  if (String(row.allowed) === '1') return 'success.main';
  return 'error.main';
}

export default function RiskPolicyDecisionLogsPage() {
  const [logs, setLogs] = useState({
    loading: false,
    error: null,
    data: { items: [], pagination: { page: 0, page_size: 20, total: 0 } },
  });
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const initialLoadPendingRef = useRef(true);
  const [filters, setFilters] = useState({
    page: 0,
    page_size: 20,
    tier_code: '',
    transaction_type: '',
    decision_code: '',
    requires_review: '',
    reference: '',
  });

  useEffect(() => {
    let active = true;

    const loadLogs = async () => {
      setLogs((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await fetchAdmin('/risk/decision_logs', filters);
        if (!active) {
          return;
        }

        setLogs({
          loading: false,
          error: null,
          data: {
            items: Array.isArray(response?.data) ? response.data : [],
            pagination: {
              page: Number(response?.pagination?.page ?? filters.page),
              page_size: Number(response?.pagination?.page_size ?? filters.page_size),
              total: Number(response?.pagination?.total ?? 0),
            },
          },
        });
      } catch (error) {
        if (!active) {
          return;
        }

        setLogs((prev) => ({
          ...prev,
          loading: false,
          error: toErrorMessage(error),
        }));
      } finally {
        if (!active) {
          return;
        }

        initialLoadPendingRef.current = false;
      }
    };

    loadLogs();

    return () => {
      active = false;
    };
  }, [filters]);

  const rows = useMemo(
    () =>
      (logs.data?.items || []).map((row) => ({
        id: row.id,
        scope: row.decision_scope,
        reference: row.reference,
        status: getStatusLabel(row),
        decision: row.decision_code,
        tier: row.tier_code,
        createdAt: row.created_at,
        raw: row,
      })),
    [logs.data]
  );

  const isInitialLoading = initialLoadPendingRef.current && logs.loading && !rows.length;
  const isRefreshing = logs.loading && !isInitialLoading;

  return (
    <AdminRouteLayout title="Decision Logs" breadcrumbTitle="Risk & Policy">
      <Stack spacing={2.5}>
        <Card variant="outlined">
          <CardContent sx={{ py: 2 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Filters
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Narrow the list without taking space away from the review table.
                </Typography>
              </Box>

              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={6} md={2.4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="transaction-type-label">Transaction type</InputLabel>
                    <Select
                      labelId="transaction-type-label"
                      label="Transaction type"
                      value={filters.transaction_type}
                      onChange={(event) =>
                        setFilters((prev) => ({
                          ...prev,
                          page: 0,
                          transaction_type: event.target.value,
                        }))
                      }
                    >
                      <MenuItem value="">All</MenuItem>
                      {TRANSACTION_TYPES.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="tier-code-label">Tier code</InputLabel>
                    <Select
                      labelId="tier-code-label"
                      label="Tier code"
                      value={filters.tier_code}
                      onChange={(event) =>
                        setFilters((prev) => ({
                          ...prev,
                          page: 0,
                          tier_code: event.target.value,
                        }))
                      }
                    >
                      <MenuItem value="">All</MenuItem>
                      {TIER_CODES.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="decision-code-label">Decision code</InputLabel>
                    <Select
                      labelId="decision-code-label"
                      label="Decision code"
                      value={filters.decision_code}
                      onChange={(event) =>
                        setFilters((prev) => ({
                          ...prev,
                          page: 0,
                          decision_code: event.target.value,
                        }))
                      }
                    >
                      <MenuItem value="">All</MenuItem>
                      {DECISION_CODES.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="requires-review-label">Requires review</InputLabel>
                    <Select
                      labelId="requires-review-label"
                      label="Requires review"
                      value={filters.requires_review}
                      onChange={(event) =>
                        setFilters((prev) => ({
                          ...prev,
                          page: 0,
                          requires_review: event.target.value,
                        }))
                      }
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="1">Needs review</MenuItem>
                      <MenuItem value="0">Resolved only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reference"
                    value={filters.reference}
                    onChange={(event) =>
                      setFilters((prev) => ({
                        ...prev,
                        page: 0,
                        reference: event.target.value,
                      }))
                    }
                  />
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        {logs.error ? (
          <AdminErrorState title="Request failed" message={logs.error} />
        ) : (
          <Paper variant="outlined" sx={{ position: 'relative', overflow: 'hidden' }}>
            {isInitialLoading ? (
              <Box sx={{ minHeight: 420, display: 'grid', placeItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ position: 'relative' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)' }}>
                  <Table stickyHeader size="medium" aria-label="decision logs table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Scope</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Reference</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Tier</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Decision</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Requires review</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Created at</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {rows.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={() => {
                            setSelectedRow(row.raw);
                            setInspectorOpen(true);
                          }}
                          sx={{
                            cursor: 'pointer',
                            '& td': { py: 1.5 },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: getStatusColor(row.raw),
                                }}
                              />
                              <Typography variant="body2" fontWeight={600}>
                                {row.status}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{row.scope}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', maxWidth: 260 }} title={row.reference}>
                            {row.reference}
                          </TableCell>
                          <TableCell>{row.tier}</TableCell>
                          <TableCell sx={{ maxWidth: 320, wordBreak: 'break-word' }}>{row.decision}</TableCell>
                          <TableCell>
                            <Checkbox checked={String(row.raw.requires_review) === '1'} disabled size="small" />
                          </TableCell>
                          <TableCell>{row.createdAt}</TableCell>
                        </TableRow>
                      ))}

                      {!rows.length && !logs.loading ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 8, textAlign: 'center' }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              No decision logs
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              The current filters returned no rows.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </TableContainer>

                {isRefreshing ? (
                  <Backdrop
                    open
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 1,
                      color: 'text.primary',
                      bgcolor: 'rgba(255,255,255,0.35)',
                    }}
                  >
                    <CircularProgress />
                  </Backdrop>
                ) : null}

                <TablePagination
                  component="div"
                  count={logs.data?.pagination?.total ?? 0}
                  page={Math.max(filters.page, 0)}
                  onPageChange={(_, newPage) =>
                    setFilters((prev) => ({
                      ...prev,
                      page: newPage,
                    }))
                  }
                  rowsPerPage={filters.page_size}
                  onRowsPerPageChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      page: 0,
                      page_size: parseInt(event.target.value, 10),
                    }))
                  }
                  rowsPerPageOptions={[20, 50, 100]}
                />
              </Box>
            )}
          </Paper>
        )}
      </Stack>

      <PolicyInspectorDrawer
        open={inspectorOpen && Boolean(selectedRow)}
        onClose={() => setInspectorOpen(false)}
        row={selectedRow}
      />
    </AdminRouteLayout>
  );
}
